// Packages
import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
    startOfMonth,
    endOfMonth,
    differenceInCalendarMonths,
    differenceInMonths,
    min,
    max,
    isAfter,
    isBefore,
    addDays,
    isSameDay,
    format,
    addMonths,
    subMonths,
} from 'date-fns';
import DayPickerProxy from '@PROJECT_ROOT/assets/responsive-frontend/js/parts/DayPickerProxy';
import { dayPickerLocales as locales } from '@PROJECT_ROOT/assets/responsive-frontend/js/services/utils';
import Tippy from '@tippyjs/react';
// Services
import { getAdriaticLangForDateFormats } from '@PROJECT_ROOT/assets/responsive-frontend/js/services/apiService';
// Constant-e
import { HORIZONTAL_ORIENTATION, VERTICAL_ORIENTATION } from '@PROJECT_ROOT/assets/responsive-frontend/js/services/constants';

// Lokalna i brža zamjena za date-fns format(date, 'yyyy-MM-dd'). Funkcija se poziva velik broj puta kroz modifiere
// kalendara pri svakom renderu, pa se izbjegava nepotreban overhead date-fns formatiranja radi boljeg INP-a.
const pad2 = (number) => (number < 10 ? `0${number}` : `${number}`);
const toDateKey = (date) => `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;

export default function DateRangePicker({
    isDatePickerShown,
    initialMonth = new Date(),
    focusedMonth,
    minDate,
    maxDate,
    availabilityDates,
    startDate,
    endDate,
    onDatesChange,
    orientation,
    visibleNumberOfMonths,
    desktopNumberOfDisplayedMonths = 2,
    disableDaySelect = false,
    notAllowedToSelectMessage,
    onMonthChange,
    ssrParameters,
    isMobileClient,
    isOldCheckoutSelection = false,
    customHorizontalScroll,
    dayInteractionDisabled,
    validationHighlight,
    minOvernightStays,
    maxOvernightStays,
    onClickDayLessThanMinOvernightStays,
    onClickDayMoreThanMaxOvernightStays,
}) {
    const langISO = ssrParameters?.lang || getAdriaticLangForDateFormats();
    const [endDateHoveredOver, setEndDateHoveredOver] = useState(null);
    const dateRangePickerWrapperRef = useRef();
    const wasDatePickerShownOnceAlready = useRef(false);
    const [month, setMonth] = useState();
    const [numberOfMonths, setNumberOfMonths] = useState(0);
    const [availableCheckOutDates, setAvailableCheckOutDates] = useState([]);
    const [tooltip, setTooltip] = useState({
        content: '',
        target: null,
        visible: false,
    });

    useEffect(() => {
        if (isDatePickerShown) {
            if (startDate || !wasDatePickerShownOnceAlready.current) {
                const to = startOfMonth(startDate ?? initialMonth);
                if (orientation === HORIZONTAL_ORIENTATION) {
                    setMonth(to);
                } else {
                    const from = startOfMonth(minDate);
                    const difference = differenceInMonths(to, from);
                    const calendarMonthElementIndex = Math.max(difference + 1, 0);
                    setTimeout(() => {
                        // querySelectorAll umjesto :nth-child jer DayPickerProxy svaki mjesec renderira kao zaseban root.
                        const calendarMonthElement = dateRangePickerWrapperRef.current?.querySelectorAll('.rdp-month')[calendarMonthElementIndex - 1];
                        calendarMonthElement?.scrollIntoView({ behavior: 'smooth' });
                    }, 300);
                }
            }
            wasDatePickerShownOnceAlready.current = true;
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDatePickerShown]);

    // Početak - logika za custom swipe kalendara.
    // NAPOMENA: Ovo je custom implementacija swip-anja miseci, razlog je taj sto trenutna verzija(7.4.10) react-day-picker-a
    // nema tu funkcionalnost.
    // Ako dođemo do verzije koja ima tu funkcionalnost, potrebno je ukloniti ovaj dio i ostali povezani kod.
    const touchStartX = useRef(null);

    const handleTouchStart = (e) => {
        touchStartX.current = e.touches[0].clientX;
    };

    const handleTouchEnd = (e) => {
        try {
            if (touchStartX.current === null) return;
            const deltaX = e.changedTouches[0].clientX - touchStartX.current;
            let newMonth = month;
            if (deltaX > 50) {
                newMonth = subMonths(month, 1);
                if (isBefore(startOfMonth(newMonth), startOfMonth(minDate))) {
                    newMonth = startOfMonth(minDate);
                }
            } else if (deltaX < -50) {
                newMonth = addMonths(month, 1);
                if (isAfter(startOfMonth(newMonth), startOfMonth(maxDate))) {
                    newMonth = startOfMonth(maxDate);
                }
            }
            if (!isSameDay(newMonth, month)) {
                setMonth(newMonth);
                if (onMonthChange) onMonthChange(newMonth);
            }
            touchStartX.current = null;
        } catch (error) {
            reportError(error);
        }
    };
    // Kraj - logika za custom swipe kalendara.

    const showTooltip = (content, target, duration) => {
        setTooltip({
            content: <span className="notranslate" translate="no">{content}</span>,
            target,
            visible: true,
        });
        setTimeout(() => {
            setTooltip((prev) => ({ ...prev, visible: false, target: null }));
        }, duration || 3000);
    };

    const groupedAvailabilityDates = useMemo(() => availabilityDates?.reduce((datesGroups, { date, check_in_status }) => {
        if (!datesGroups[check_in_status]) {
            datesGroups[check_in_status] = new Set();
        }
        datesGroups[check_in_status].add(date);
        return datesGroups;
    }, {}), [availabilityDates]);

    const notSelectableDates = groupedAvailabilityDates?.not_selectable || new Set();

    const groupedDatesFunctions = useMemo(() => {
        if (!groupedAvailabilityDates) return {};
        return Object.fromEntries(
            Object.entries(groupedAvailabilityDates).map(([check_in_status, dateSet]) => [
                check_in_status,
                (day) => {
                    if (startDate && isSameDay(day, startDate)) return false;
                    if (endDate && isSameDay(day, endDate)) return false;
                    if (startDate && endDate && !isBefore(day, startDate) && !isAfter(day, endDate)) {
                        return false;
                    }
                    return dateSet?.has(toDateKey(day));
                },
            ]),
        );
    }, [groupedAvailabilityDates, startDate, endDate]);

    useEffect(() => {
        if (focusedMonth) {
            setMonth(startOfMonth(focusedMonth));
        } else if (orientation === VERTICAL_ORIENTATION) {
            setMonth(minDate);
            const from = startOfMonth(minDate);
            const to = endOfMonth(maxDate);
            setNumberOfMonths(differenceInCalendarMonths(to, from) + 1);
        } else {
            setMonth(startDate ?? initialMonth);
            setNumberOfMonths(desktopNumberOfDisplayedMonths);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [focusedMonth, orientation]);

    // Na promjenu "availabilityDates" provjeravamo postoji li startDate, nakon toga provjeravamo i postavlajmo moguće "check out dane".
    // Logika je dodana zbog toga što se na inicijalni load(kad već postoje odabrani datumi) nisu ažurirali checkout dani kao kod "handleDayClick".
    useEffect(() => {
        if (startDate) {
            const startStr = toDateKey(startDate);
            const foundDay = availabilityDates?.find((d) => d.date === startStr);
            if (foundDay?.availableCheckOutDatesInNext30Days?.length) {
                setAvailableCheckOutDates(foundDay.availableCheckOutDatesInNext30Days);
            }
        }
    // eslint-disable-next-line
    }, [availabilityDates]);

    const isDateRangeAvailabile = (from, to) => {
        if (!groupedAvailabilityDates) return true;
        let dateToCheck = addDays(from, 1);
        while (isBefore(dateToCheck, to)) {
            if (groupedAvailabilityDates.unavailable?.has(toDateKey(dateToCheck))) {
                return false;
            }
            dateToCheck = addDays(dateToCheck, 1);
        }
        return true;
    };

    // Disable-an eslint jer metodu koristi npm package.
    // eslint-disable-next-line default-param-last
    const handleDayClick = (clickedDayDate, modifiers = {}, event) => {
        if (!onDatesChange || disableDaySelect) return;
        const clickedDateStr = toDateKey(clickedDayDate);
        const isStartSelected = Boolean(startDate && !endDate);
        const isInAvailableCheckOutDates = isStartSelected && availableCheckOutDates.includes(clickedDateStr);
        if (startDate) {
            if (minOvernightStays && modifiers.notAvailableCheckoutAfterStart) {
                onClickDayLessThanMinOvernightStays?.();
            } else if (maxOvernightStays && modifiers.notAvailableCheckoutAfterStartAndNotInRange) {
                onClickDayMoreThanMaxOvernightStays?.();
            }
        }
        if (isSameDay(clickedDayDate, startDate)) {
            onDatesChange(null, null);
            setEndDateHoveredOver(null);
            return;
        }
        if ((modifiers.visuallyDisabled || modifiers.unavailable || modifiers.notAvailableCheckoutAfterStart || modifiers.notAvailableCheckoutAfterStartAndNotInRange || (!startDate && modifiers.checkout_only)) && !isInAvailableCheckOutDates) {
            if (notAllowedToSelectMessage && event?.target) {
                showTooltip(notAllowedToSelectMessage, event.target, 3000);
            }
            return;
        }
        if (endDate && isSameDay(clickedDayDate, endDate)) {
            onDatesChange(startDate, null);
            setEndDateHoveredOver(null);
            return;
        }
        if (startDate && !endDate) {
            if (isBefore(clickedDayDate, startDate)) {
                if (notAllowedToSelectMessage && event?.target) {
                    showTooltip(notAllowedToSelectMessage, event.target, 3000);
                }
                return;
            }
            if (!isOldCheckoutSelection && availabilityDates && !availableCheckOutDates.includes(clickedDateStr)) {
                if (notAllowedToSelectMessage && event?.target) {
                    showTooltip(notAllowedToSelectMessage, event.target, 3000);
                }
                return;
            }
            if (isDateRangeAvailabile(startDate, clickedDayDate)) {
                onDatesChange(startDate, clickedDayDate);
            } else if (notAllowedToSelectMessage) {
                showTooltip(notAllowedToSelectMessage, event.target, 3000);
            }
        } else {
            onDatesChange(clickedDayDate, null);
            if (isOldCheckoutSelection || !availabilityDates) return;
            const foundDay = availabilityDates.find((d) => d.date === clickedDateStr);
            if (foundDay?.availableCheckOutDatesInNext30Days?.length) {
                setAvailableCheckOutDates(foundDay.availableCheckOutDatesInNext30Days);
            } else {
                setAvailableCheckOutDates([]);
            }
        }
    };

    const handleDayMouseEnter = (date) => {
        setEndDateHoveredOver(startDate && !endDate ? date : null);
    };

    const modifiers = {
        visuallyDisabled: (day) => {
            if (isBefore(day, minDate) || isAfter(day, maxDate)) return true;
            const dateStr = toDateKey(day);
            if (startDate && isSameDay(day, startDate)) return false;
            if (endDate && isSameDay(day, endDate)) return false;
            if (startDate && endDate) {
                if (!isBefore(day, startDate) && !isAfter(day, endDate)) {
                    return false;
                }
            }
            if (!isOldCheckoutSelection && startDate && !endDate) {
                if (availabilityDates) {
                    return !availableCheckOutDates.includes(dateStr);
                }
                if (minOvernightStays) {
                    const minPossibleCheckoutDate = addDays(startDate, minOvernightStays);
                    const isEnoughOvernightStays = day >= minPossibleCheckoutDate;
                    if (!isEnoughOvernightStays) return true;
                }
                if (maxOvernightStays) {
                    const maxPossibleCheckoutDate = addDays(startDate, maxOvernightStays);
                    const isTooManyOvernightStays = day > maxPossibleCheckoutDate;
                    if (isTooManyOvernightStays) return true;
                }
            }
            if (notSelectableDates?.has(dateStr)) return true;
            return groupedAvailabilityDates?.unavailable?.has(dateStr);
        },
        weekend: (day) => {
            const weekDayIndex = day.getDay();
            return weekDayIndex === 0 || weekDayIndex === 6;
        },
        notSelectableDates: (day) => notSelectableDates?.has(toDateKey(day)),
        ...groupedDatesFunctions,
        startDate: startDate ? (day) => isSameDay(day, startDate) : undefined,
        endDate: endDate ? (day) => isSameDay(day, endDate) : undefined,
        hoverSelected: (day) => {
            if (!startDate || !endDateHoveredOver) return false;
            if (isBefore(endDateHoveredOver, startDate)) return false;
            const from = min([startDate, endDateHoveredOver]);
            const to = max([startDate, endDateHoveredOver]);
            if (!isDateRangeAvailabile(from, to)) return false;
            if (!endDate) {
                if (minOvernightStays) {
                    const minPossibleCheckoutDate = addDays(startDate, minOvernightStays);
                    const isEnoughOvernightStays = day >= minPossibleCheckoutDate;
                    if (!isEnoughOvernightStays) return false;
                }
                if (maxOvernightStays) {
                    const maxPossibleCheckoutDate = addDays(startDate, maxOvernightStays);
                    const isTooManyOvernightStays = day > maxPossibleCheckoutDate;
                    if (isTooManyOvernightStays) return false;
                }
            }
            return !isBefore(day, from) && !isAfter(day, to);
        },
        endDateHoveredOver: endDateHoveredOver ? (day) => {
            if (!startDate || isBefore(endDateHoveredOver, startDate)) return false;
            const from = min([startDate, endDateHoveredOver]);
            const to = max([startDate, endDateHoveredOver]);
            if (!isDateRangeAvailabile(from, to)) return false;
            return isSameDay(day, endDateHoveredOver);
        } : undefined,
        inSelectedRange: (day) => {
            if (!startDate || !endDate) return false;
            if (isSameDay(day, startDate) || isSameDay(day, endDate)) return false;
            return isAfter(day, startDate) && isBefore(day, endDate);
        },
        validationSelectableHighlight: (day) => {
            if (!validationHighlight) return false;
            if (modifiers.visuallyDisabled(day)) return false;

            return true;
        },
        ...(minOvernightStays && {
            notAvailableCheckoutAfterStart: (day) => {
                if (!startDate || endDate) return false;
                const minPossibleCheckoutDate = addDays(startDate, minOvernightStays);
                return day < minPossibleCheckoutDate && day > startDate;
            },
        }),
        ...(maxOvernightStays && {
            notAvailableCheckoutAfterStartAndNotInRange: (day) => {
                if (!startDate || endDate) return false;
                const maxPossibleCheckoutDate = addDays(startDate, maxOvernightStays);
                return day > maxPossibleCheckoutDate;
            },
        }),
        ...(!isOldCheckoutSelection && availabilityDates && {
            notAvailableCheckoutBeforeStart: (day) => {
                if (!startDate || endDate) return false;
                const dateStr = toDateKey(day);
                return !availableCheckOutDates.includes(dateStr) && isBefore(day, startDate);
            },
            notAvailableCheckoutAfterStart: (day) => {
                if (!startDate || endDate || !availableCheckOutDates.length) return false;
                const dateStr = toDateKey(day);
                const lastAvailableDate = new Date(availableCheckOutDates[availableCheckOutDates.length - 1]);
                return (!availableCheckOutDates.includes(dateStr)
                    && isAfter(day, startDate)
                    && !isAfter(day, lastAvailableDate)
                );
            },
            notAvailableCheckoutAfterStartAndNotInRange: (day) => {
                if (!startDate || endDate || !availableCheckOutDates.length) return false;
                const dateStr = toDateKey(day);
                return !availableCheckOutDates.includes(dateStr);
            },
            firstOfMonthInRange: (day) => {
                if (!startDate || (!endDate && !endDateHoveredOver)) return false;
                const end = endDate || endDateHoveredOver;
                if (isBefore(day, startDate) || isAfter(day, end)) return false;
                return day.getDate() === 1;
            },
            lastOfMonthInRange: (day) => {
                if (!startDate || (!endDate && !endDateHoveredOver)) return false;
                const end = endDate || endDateHoveredOver;
                if (isBefore(day, startDate) || isAfter(day, end)) return false;
                const nextDay = addDays(day, 1);
                return nextDay.getDate() === 1;
            },
            availableCheckout: (day) => {
                if (!startDate || endDate) return false;
                const dateStr = toDateKey(day);
                return availableCheckOutDates.includes(dateStr);
            },
        }),
    };

    const renderDayView = (date) => {
        if (isOldCheckoutSelection || !availabilityDates) {
            return (
                <span className="day-picker__box" translate="no">
                    <span className="day-picker__date" translate="no">{date.getDate()}</span>
                </span>
            );
        }
        const dateStr = toDateKey(date);
        const found = availabilityDates?.find((d) => d.date === dateStr);
        const price = found?.price;
        const isAvailable = Boolean(found);
        let trueEndDate = endDate;
        if (startDate && endDate && isAfter(startDate, endDate)) {
            trueEndDate = startDate;
        }
        const isRealEndDate = trueEndDate && isSameDay(date, trueEndDate);
        const isHoveringEndDate = !isMobileClient
            && isAvailable
            && !endDate
            && endDateHoveredOver
            && isSameDay(date, endDateHoveredOver);
        const hidePrice = isRealEndDate || isHoveringEndDate;

        return (
            <span className="day-picker__box" translate="no">
                <span className="day-picker__date" translate="no">{date.getDate()}</span>
                <span className="day-picker__price" translate="no">
                    {hidePrice ? '-' : (price || '-')}
                </span>
            </span>
        );
    };

    const modifiersClassNames = {};
    Object.keys(modifiers).forEach((key) => {
        modifiersClassNames[key] = `rdp-day--${key}`;
    });

    // DESC: Custom rješenje za mađarski jezik zbog problema formatiranja na određenim jezicima (https://github.com/gpbl/react-day-picker/blob/main/packages/react-day-picker/CHANGELOG.md)
    const customHungarianMonthYearFormat = langISO === 'hu'
        ? (date) => format(date, 'yyyy. LLLL', { locale: locales.hu })
        : null;

    return (
        <div
            className={`day-picker__wrapper day-picker__wrapper--${orientation}`}
            ref={dateRangePickerWrapperRef}
            onMouseLeave={() => setEndDateHoveredOver(null)}
            onTouchStart={customHorizontalScroll ? handleTouchStart : undefined}
            onTouchEnd={customHorizontalScroll ? handleTouchEnd : undefined}
            translate="no"
        >
            <DayPickerProxy
                className={`day-picker day-picker--${orientation} ${dayInteractionDisabled ? 'day-picker--day-interaction-disabled' : ''}`}
                animate={true}
                mode="range"
                min={0}
                max={2}
                month={month}
                navLayout="around"
                disableNavigation={orientation === VERTICAL_ORIENTATION}
                hideNavigation={orientation === VERTICAL_ORIENTATION}
                selected={{ from: startDate, to: endDate }}
                // Ostaviti onSelect ovako zbog buga u react-day-picker paketu.
                onSelect={() => {}}
                onDayClick={handleDayClick}
                onDayMouseEnter={handleDayMouseEnter}
                numberOfMonths={visibleNumberOfMonths || numberOfMonths}
                modifiers={modifiers}
                modifiersClassNames={modifiersClassNames}
                startMonth={minDate}
                endMonth={maxDate}
                locale={locales[langISO]}
                formatters={{
                    formatDay: renderDayView,
                    ...(customHungarianMonthYearFormat && {
                        formatCaption: customHungarianMonthYearFormat,
                    }),
                }}
                labels={{
                    ...(customHungarianMonthYearFormat && {
                        labelGrid: customHungarianMonthYearFormat,
                    }),
                }}
                onMonthChange={(newMonth) => {
                    setMonth(newMonth);
                    if (onMonthChange) onMonthChange(newMonth);
                }}
            />
            {tooltip.visible && tooltip.target && (
                <Tippy
                    zIndex={9999999}
                    content={tooltip.content}
                    visible={tooltip.visible}
                    placement="top"
                    className="day-picker__tooltip"
                    getReferenceClientRect={() => tooltip.target.getBoundingClientRect()}
                >
                    <span translate="no" />
                </Tippy>
            )}
        </div>
    );
}
